import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, useAuth } from '../App';
import { useRBAC } from '../hooks/useRBAC';
import toast from 'react-hot-toast';
import DemoPackCard from '../components/DemoPackCard';

const fmt = (n) => (n || 0).toLocaleString('en-US');
const money = (n) => '$' + fmt(Math.round(n || 0));

export default function InvestorProveIt() {
  const navigate = useNavigate();
  const { isSuperAdmin, isInvestor } = useRBAC();
  const { user } = useAuth();
  const restartTour = async () => {
    localStorage.setItem(`vs_tour_${user?.email}`, JSON.stringify({ step: 0, done: false }));
    try { await api.put('/api/me/tour', { done: false, step: 0 }); } catch {}
    const saved = JSON.parse(localStorage.getItem('sls_user') || '{}');
    localStorage.setItem('sls_user', JSON.stringify({ ...saved, tour_done: false }));
    window.location.href = '/prove-it';
  };
  const [markets, setMarkets] = useState([]);
  const [market, setMarket] = useState('nyc_all');
  const [usage, setUsage] = useState(null);
  const [roi, setRoi] = useState(null);
  const [job, setJob] = useState(null);
  const [jobId, setJobId] = useState(null);
  const [running, setRunning] = useState(false);
  const [invites, setInvites] = useState([]);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [emailTo, setEmailTo] = useState('');
  const [emailBusy, setEmailBusy] = useState(false);

  const emailPdf = async (e) => {
    e.preventDefault();
    setEmailBusy(true);
    try {
      const r = await api.post(`/api/investor/prove-it/${jobId}/email`, { to: emailTo });
      if (r.data.status === 'simulated') toast(`Report queued for ${emailTo} — SIMULATED until an email key is added`, { icon: '📨', duration: 6000 });
      else if (r.data.status === 'sent') toast.success(`Proof report emailed to ${emailTo}`);
      else toast.error(r.data.message || 'Email failed');
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not email report'); }
    setEmailBusy(false);
  };
  const pollRef = useRef(null);

  const loadMeta = async () => {
    try {
      const [m, u, r] = await Promise.all([
        api.get('/api/investor/markets'),
        api.get('/api/investor/usage'),
        api.get('/api/investor/roi'),
      ]);
      setMarkets(m.data.markets || []);
      setUsage(u.data);
      setRoi(r.data);
    } catch {}
  };

  useEffect(() => { loadMeta(); return () => clearInterval(pollRef.current); }, []);
  useEffect(() => { if (isSuperAdmin) loadInvites(); }, [isSuperAdmin]); // eslint-disable-line

  const loadInvites = async () => {
    try { const r = await api.get('/api/admin/investor/invites'); setInvites(r.data.invites || []); } catch {}
  };

  const createInvite = async () => {
    try {
      const r = await api.post('/api/admin/investor/invites', { days: 7 });
      navigator.clipboard.writeText(r.data.url).catch(() => {});
      toast.success('Invite link created & copied — valid 7 days');
      loadInvites();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create invite'); }
  };

  const revokeInvite = async (token) => {
    try { await api.delete(`/api/admin/investor/invites/${token}`); loadInvites(); toast.success('Invite revoked'); } catch {}
  };

  const downloadPdf = async () => {
    if (!jobId) return;
    setPdfBusy(true);
    try {
      const r = await api.get(`/api/investor/prove-it/${jobId}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Samaritan_Solaire_Proof_Report.pdf';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Could not generate the PDF'); }
    setPdfBusy(false);
  };

  const runProof = async () => {
    setRunning(true);
    setJob({ status: 'running', stage: 'scanning' });
    try {
      const res = await api.post('/api/investor/prove-it', { market });
      const newJobId = res.data.job_id;
      setJobId(newJobId);
      pollRef.current = setInterval(async () => {
        try {
          const s = await api.get(`/api/investor/prove-it/${newJobId}`);
          setJob(s.data);
          if (s.data.status !== 'running') {
            clearInterval(pollRef.current);
            setRunning(false);
            loadMeta();
            if (s.data.status === 'done') toast.success('Live proof complete — real leads verified!');
            else toast.error(s.data.error || 'Proof run failed');
          }
        } catch {}
      }, 2500);
    } catch (err) {
      setRunning(false);
      setJob(null);
      toast.error(err.response?.data?.detail || 'Could not start the proof run');
    }
  };

  const s = job?.summary;
  const stages = [
    { key: 'scanning', label: 'Scanning live permit feeds' },
    { key: 'verifying', label: 'Verifying owner contacts' },
    { key: 'done', label: 'Building ROI report' },
  ];
  const stageIdx = stages.findIndex(x => x.key === job?.stage);

  return (
    <div className="space-y-8 animate-fade-in" data-testid="prove-it-page">
      {/* Hero */}
      <div className="rounded-3xl bg-gradient-to-br from-slate-900 via-slate-800 to-emerald-900 p-8 sm:p-10 text-white relative overflow-hidden">
        <div className="relative z-10 max-w-3xl">
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/20 border border-emerald-400/30 text-emerald-300 text-[11px] font-bold uppercase tracking-widest mb-4">
            <i className="fa-solid fa-rocket" /> Investor Live Proof
          </span>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold mb-3">See real leads, verified live — in about 2 minutes</h1>
          <p className="text-slate-300 text-sm sm:text-base">
            One click scans live building-permit activity in your chosen market, pulls real homeowner names,
            verifies callable phone numbers and emails, and shows you exactly what this pipeline is worth versus the monthly rental cost.
          </p>
        </div>
        <i className="fa-solid fa-bolt absolute -right-6 -bottom-8 text-[180px] text-emerald-500/10" />
        {isInvestor && (
          <button onClick={restartTour} className="absolute top-6 right-6 z-10 px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 text-xs font-semibold text-white border border-white/20" data-testid="restart-tour-btn"><i className="fa-solid fa-route mr-1.5" />Restart walkthrough</button>
        )}
      </div>

      <DemoPackCard />

      {/* Usage meter (investor only) */}
      {usage?.metered && (
        <div className="grid sm:grid-cols-2 gap-4" data-testid="usage-meter">
          {[
            { label: 'Trial Scans', used: usage.scans_used, limit: usage.scans_limit, icon: 'fa-tower-broadcast' },
            { label: 'Contact Verifications', used: usage.enrichments_used, limit: usage.enrichments_limit, icon: 'fa-user-check' },
          ].map((m) => (
            <div key={m.label} className="bg-surface rounded-2xl border border-border p-5">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-bold uppercase tracking-widest text-text-muted"><i className={`fa-solid ${m.icon} mr-2 text-primary`} />{m.label}</span>
                <span className="text-sm font-bold text-text-primary tabular-nums">{m.used} / {m.limit}</span>
              </div>
              <div className="h-2 rounded-full bg-surface-alt overflow-hidden">
                <div className="h-full bg-gradient-to-r from-primary to-emerald-500 transition-all" style={{ width: `${Math.min(100, (m.used / (m.limit || 1)) * 100)}%` }} />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Market picker + run */}
      <div className="bg-surface rounded-2xl border border-border p-6">
        <h2 className="font-heading text-lg font-bold text-text-primary mb-1">1. Pick a live market</h2>
        <p className="text-xs text-text-muted mb-4">NY boroughs + surrounding markets — residential, private &amp; commercial permit activity</p>
        <div className="flex flex-wrap gap-2 mb-6">
          {markets.map(m => (
            <button
              key={m.key}
              onClick={() => setMarket(m.key)}
              disabled={running}
              className={`px-4 py-2 rounded-full text-sm font-semibold border transition-all ${market === m.key ? 'bg-primary text-white border-primary shadow-glow-green' : 'bg-surface-alt text-text-secondary border-border hover:border-primary/40'}`}
              data-testid={`market-${m.key}`}
            >
              {m.label}
            </button>
          ))}
        </div>
        <button
          onClick={runProof}
          disabled={running}
          className="w-full sm:w-auto px-8 py-4 rounded-2xl bg-gradient-to-r from-primary to-emerald-600 text-white font-bold text-base shadow-glow-green hover:brightness-110 active:scale-[0.98] transition-all disabled:opacity-60"
          data-testid="run-proof-btn"
        >
          {running ? (<><i className="fa-solid fa-circle-notch fa-spin mr-2" />Running live proof…</>) : (<><i className="fa-solid fa-play mr-2" />2. Run Live Proof</>)}
        </button>
      </div>

      {/* Progress */}
      {job && job.status === 'running' && (
        <div className="bg-surface rounded-2xl border border-border p-6" data-testid="proof-progress">
          <div className="flex flex-col sm:flex-row gap-4">
            {stages.map((st, i) => (
              <div key={st.key} className={`flex-1 flex items-center gap-3 p-4 rounded-xl border ${i <= stageIdx ? 'border-primary/40 bg-primary/5' : 'border-border bg-surface-alt'}`}>
                {i < stageIdx ? <i className="fa-solid fa-circle-check text-primary" /> : i === stageIdx ? <i className="fa-solid fa-circle-notch fa-spin text-primary" /> : <i className="fa-regular fa-circle text-text-muted" />}
                <span className={`text-sm font-medium ${i <= stageIdx ? 'text-text-primary' : 'text-text-muted'}`}>{st.label}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Results */}
      {job?.status === 'done' && s && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="proof-results">
            {[
              { label: 'Live Leads Found', value: fmt(s.leads_found), icon: 'fa-house-signal', color: 'text-primary' },
              { label: 'Verified Callable Contacts', value: fmt(s.contacts_verified), icon: 'fa-phone-volume', color: 'text-emerald-500' },
              { label: 'Emails Discovered', value: fmt(s.emails_found), icon: 'fa-envelope-circle-check', color: 'text-sky-500' },
              { label: 'Hot / High Opportunities', value: fmt(s.hot_leads), icon: 'fa-fire', color: 'text-orange-500' },
            ].map(k => (
              <div key={k.label} className="bg-surface rounded-2xl border border-border p-5">
                <i className={`fa-solid ${k.icon} ${k.color} text-lg mb-3 block`} />
                <p className="font-heading text-2xl font-bold text-text-primary tabular-nums">{k.value}</p>
                <p className="text-[11px] font-semibold uppercase tracking-wider text-text-muted mt-1">{k.label}</p>
              </div>
            ))}
          </div>

          {/* ROI panel */}
          <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-6" data-testid="roi-panel">
            <h3 className="font-heading text-lg font-bold text-text-primary mb-4"><i className="fa-solid fa-scale-balanced text-emerald-500 mr-2" />Is it worth renting?</h3>
            <div className="grid sm:grid-cols-3 gap-6">
              <div>
                <p className="text-[11px] font-bold uppercase tracking-widest text-text-muted mb-1">Pipeline value (this run)</p>
                <p className="font-heading text-3xl font-bold text-text-primary tabular-nums">{money(s.pipeline_value)}</p>
              </div>
              <div>
                <p className="text-[11px] font-bold uppercase tracking-widest text-text-muted mb-1">Est. revenue @ 10% close</p>
                <p className="font-heading text-3xl font-bold text-emerald-600 tabular-nums">{money(s.est_revenue_10pct_close)}</p>
              </div>
              <div>
                <p className="text-[11px] font-bold uppercase tracking-widest text-text-muted mb-1">Monthly rental cost</p>
                <p className="font-heading text-3xl font-bold text-text-primary tabular-nums">{money(s.rental_price)}</p>
                <p className="text-sm font-bold text-emerald-600 mt-1"><i className="fa-solid fa-arrow-trend-up mr-1" />{s.roi_multiple}x return</p>
              </div>
            </div>
          </div>

          {/* Lead list */}
          <div className="bg-surface rounded-2xl border border-border overflow-hidden">
            <div className="px-6 py-4 border-b border-border flex items-center justify-between gap-3 flex-wrap">
              <h3 className="font-heading font-bold text-text-primary">Real leads from {s.market}</h3>
              <div className="flex items-center gap-3">
                <button onClick={downloadPdf} disabled={pdfBusy} className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-slate-900 text-white text-xs font-bold hover:brightness-125 transition-all disabled:opacity-60" data-testid="download-proof-pdf-btn">
                  {pdfBusy ? <i className="fa-solid fa-circle-notch fa-spin" /> : <i className="fa-solid fa-file-pdf text-red-400" />}
                  Download Proof Report (PDF)
                </button>
                <form onSubmit={emailPdf} className="flex items-center gap-1.5" data-testid="email-proof-form">
                  <input type="email" required value={emailTo} onChange={e => setEmailTo(e.target.value)} placeholder="investor@fund.com" className="input-field h-8 text-xs w-44" data-testid="email-proof-input" />
                  <button type="submit" disabled={emailBusy} className="inline-flex items-center gap-1.5 px-3 h-8 rounded-xl text-xs font-bold text-[#0C2417] disabled:opacity-60" style={{ background: '#FAAB1C' }} data-testid="email-proof-btn">{emailBusy ? <i className="fa-solid fa-circle-notch fa-spin" /> : <i className="fa-solid fa-paper-plane" />}Email report</button>
                </form>
                <button onClick={() => navigate('/leads')} className="text-xs font-semibold text-primary hover:underline" data-testid="view-all-leads-btn">Open full Lead Database →</button>
              </div>
            </div>
            <div className="divide-y divide-border">
              {(job.leads || []).map((l, i) => (
                <div key={l.id || i} className="px-6 py-4 flex flex-wrap items-center gap-4 hover:bg-surface-alt/50 transition-colors" data-testid="proof-lead-row">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-text-primary truncate">{l.owner_name || l.name}</p>
                    <p className="text-xs text-text-muted truncate">{l.address}{l.city ? `, ${l.city}` : ''} {l.state} {l.zip}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {(l.opportunity_tier || '').toUpperCase() === 'HOT' && <span className="px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 text-[10px] font-bold uppercase">Hot</span>}
                    <span className="px-2 py-0.5 rounded-full bg-primary/10 text-primary text-[10px] font-bold">Score {l.opportunity_score || l.score || '—'}</span>
                    {l.phone_callable ? (
                      <a href={`tel:${l.phone}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-500 text-white text-xs font-bold hover:brightness-110" data-testid="proof-call-btn">
                        <i className="fa-solid fa-phone" /> {l.phone}
                      </a>
                    ) : l.email ? (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-sky-50 text-sky-700 text-xs font-semibold border border-sky-200">
                        <i className="fa-solid fa-envelope" /> {l.email}
                      </span>
                    ) : (
                      <span className="text-[10px] text-text-muted px-2 py-1 rounded-full bg-surface-alt border border-border">contact not verified</span>
                    )}
                    <button onClick={() => navigate(`/leads/${l.id}`)} className="text-xs font-semibold text-text-muted hover:text-primary px-2" data-testid="proof-lead-open">Details</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {job?.status === 'error' && (
        <div className="rounded-2xl border border-destructive/30 bg-destructive/5 p-6 text-sm text-destructive" data-testid="proof-error">
          <i className="fa-solid fa-triangle-exclamation mr-2" />{job.error || 'The proof run failed. Try another market.'}
        </div>
      )}

      {/* Admin: investor invite links */}
      {isSuperAdmin && (
        <div className="bg-surface rounded-2xl border border-border p-6" data-testid="invite-manager">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <div>
              <h3 className="font-heading text-lg font-bold text-text-primary"><i className="fa-solid fa-envelope-open-text text-primary mr-2" />Invite an Investor</h3>
              <p className="text-xs text-text-muted mt-1">Shareable 7-day link that auto-creates a metered trial account (10 scans / 40 verifications)</p>
            </div>
            <button onClick={createInvite} className="btn-primary text-sm" data-testid="create-invite-btn"><i className="fa-solid fa-link mr-1.5" />Create Invite Link</button>
          </div>
          {invites.length > 0 && (
            <div className="divide-y divide-border border-t border-border">
              {invites.map(inv => {
                const expired = new Date(inv.expires_at) < new Date();
                const status = inv.revoked ? 'Revoked' : inv.used_by ? `Claimed by ${inv.used_by}` : expired ? 'Expired' : 'Active';
                return (
                  <div key={inv.token} className="py-3 flex flex-wrap items-center gap-3" data-testid="invite-row">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${status === 'Active' ? 'bg-emerald-100 text-emerald-700' : inv.used_by ? 'bg-sky-100 text-sky-700' : 'bg-slate-100 text-slate-500'}`}>{status}</span>
                    <span className="text-xs font-mono text-text-muted truncate flex-1 min-w-[200px]">{inv.url}</span>
                    <span className="text-[10px] text-text-muted">expires {new Date(inv.expires_at).toLocaleDateString()}</span>
                    {status === 'Active' && (
                      <>
                        <button onClick={() => { navigator.clipboard.writeText(inv.url); toast.success('Link copied'); }} className="text-xs font-semibold text-primary hover:underline" data-testid="copy-invite-btn"><i className="fa-solid fa-copy mr-1" />Copy</button>
                        <button onClick={() => revokeInvite(inv.token)} className="text-xs font-semibold text-destructive hover:underline" data-testid="revoke-invite-btn">Revoke</button>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Platform-wide ROI */}
      {roi && (
        <div className="bg-surface rounded-2xl border border-border p-6" data-testid="platform-roi">
          <h3 className="font-heading text-lg font-bold text-text-primary mb-4"><i className="fa-solid fa-chart-line text-primary mr-2" />Whole-platform snapshot</h3>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-5">
            {[
              { label: 'Total Leads', value: fmt(roi.total_leads) },
              { label: 'Verified Callable', value: fmt(roi.verified_callable) },
              { label: 'Pipeline Value', value: money(roi.pipeline_value) },
              { label: 'Est. Revenue @10%', value: money(roi.est_revenue_10pct_close) },
              { label: 'Cost / Verified Contact', value: roi.cost_per_verified_contact ? money(roi.cost_per_verified_contact) : '—' },
            ].map(k => (
              <div key={k.label}>
                <p className="font-heading text-xl font-bold text-text-primary tabular-nums">{k.value}</p>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-text-muted mt-0.5">{k.label}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
